import ListInputs from './ListInputs'
import SunEditor from 'suneditor-react'
import AdminAppBar from './AdminAppBar'
import { useEffect, useState } from 'react'
import 'suneditor/dist/css/suneditor.min.css'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory, useLocation } from 'react-router'
import MenuInputs from '../../../components/MenuInputs'
import Alert, { ActionsOptType } from '../../../components/Alert'
import { saveTemplate, updateTemplate, deleteTemplate } from '../../../services/templates'
import { Container, Paper, Button, Typography, TextField, MenuItem } from '@material-ui/core'
import { TemplateStatusMap, TemplateTypesMap, TEMPLATE_STATUS, TEMPLATE_TYPES, IBlock, IInput, ITemplate } from '../../../types/templates'
// import { align, fontColor, hiliteColor, list, table, formatBlock, textStyle, image, imageGallery } from 'suneditor/src/plugins'
// import ListBlocks from './ListBlock'
// import MenuBlocks from '../../../components/MenuBlocks'
// import Alert, { ActionsOptType } from '../../../components/Alert'

/**
 * define styles for the component
*/
const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
  },
  row: {
    display: 'flex',
    flex: 1,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  paper: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  submit: {
    // position: 'absolute',
    //bottom: 0,
    marginTop: theme.spacing(2),
    maxWidth: '50%',
    color: '#FFFF',
    alignSelf: 'center',
    backgroundColor: theme.palette.secondary.dark,
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(2),
    display: 'flex',
  },
}))

/**
 * Custom `TemplateEditor` component declaration.
 * This component is used to create custom templates by the adminUser
 * TemplateEditor integrates the components created in adminPanel
 * This component creats methods to save, edit and delete templates
 * This component integrates the react component sunEditor the buttons that will be used 
*/
const TemplateEditor = (): JSX.Element => {
  const classes = useStyles()
  const location = useLocation()
  const history = useHistory()
  const TemplateStatus = Array.from(TemplateStatusMap.values())
  const TemplateTypes = Array.from(TemplateTypesMap.values())
  const [alertOptions, setAlertOptions] = useState<ActionsOptType>({
    message: null,
    type: null,
    open: false,
  })
  const template: ITemplate | null = location.state
    ? (location.state as ITemplate)
    : null
  const [templateStatus, setTemplateStatus] = useState<string>(
    template?.template_status || TEMPLATE_STATUS.DRAFT
  )
  const [templateTypes, setTemplateTypes] = useState<string>(
    template?.template_types || TEMPLATE_TYPES.OTHERS
  )
  const [templateName, setTemplateName] = useState<string>(template?.name || '')
  const [templatePrice, setTemplatePrice] = useState<number>(template?.price || 0)
  const [templateDescription, setTemplateDescription] = useState<string>(
    template?.description || ''
  )
  const [blocks, setBlocks] = useState<IBlock[]>(
    (template?.template_data?.blocks || []) as IBlock[]
  )
  const [inputs, setInputs] = useState<IInput[]>(
    (template?.template_data?.inputs || []) as IInput[]
  )
  const [HTMLCode, setHTMLCode] = useState<string>(
    template?.template_data?.HTMLCode || ''
  )
  const templateStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateStatus(event.target.value)
  }
  const templateTypesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateTypes(event.target.value)
  }

  useEffect(() => {
    if (template) {
      setBlocks(template.template_data?.blocks || [])
    }
  }, [template])

  /**
   * This method is used to save template data or update template data
   * @return {Void}
  */
  const onSubmit = async () => {
    if (template) {
      const { data, error } = await updateTemplate({
        id: template.id,
        name: templateName,
        description: templateDescription,
        price: templatePrice,
        template_status_id: TemplateStatusMap.get(templateStatus).id,
        template_type_id: TemplateTypesMap.get(templateTypes).id,
        template_data: { blocks, inputs, HTMLCode },
      })
      if (data)
        setAlertOptions({
          message: 'Se ha modificado la plantilla',
          open: true,
          type: 'info',
        })
      if (error) setAlertOptions({ message: error, open: true, type: 'error' })
    } else {
      const { data, error } = await saveTemplate({
        name: templateName,
        description: templateDescription,
        price: templatePrice,
        template_status_id: TemplateStatusMap.get(templateStatus).id,
        template_type_id: TemplateTypesMap.get(templateTypes).id,
        template_data: { blocks, inputs, HTMLCode },
      })
      if (data) {
        setAlertOptions({
          message: 'Se ha creado la plantilla',
          open: true,
          type: 'info',
        })
        history.push('/docengine')
      }
      if (error) setAlertOptions({ message: error, open: true, type: 'error' })
    }
  }

  /**
   * This method is used to delete template
   * @return {Void}
  */
  const onDelete = async () => {
    const { data, error } = await deleteTemplate({
      id: template?.id,
    })
    if (data)
      setAlertOptions({
        message: 'Se ha elimnado la plantilla',
        open: true,
        type: 'info',
      })
    history.push('/docengine')
    if (error) setAlertOptions({ message: error, open: true, type: 'error' })
  }

  /**
   * This method is used to update Html
   * @return {Void}
  */
  const handleChange = (content: string) => {
    setHTMLCode(content)
  }

  return (
    <>
      <AdminAppBar />
      <Alert alertOptions={alertOptions} setAlertOptions={setAlertOptions} />
      <Container className={classes.container} maxWidth="xl">
        <Typography
          variant="h5"
          component="h5"
          align="center"
          style={{ marginBottom: '2vh' }}
        >
          {template ? 'EDITAR PLANTILLA' : 'NUEVA PLANTILLA'}
        </Typography>
        <Paper className={classes.paper}>
          <div className={classes.row}>
            <TextField
              className={classes.textField}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTemplateName(e.target.value)
              }
              style={{ flex: 0.5 }}
              id="name"
              value={templateName}
              label="Nombre"
              variant="outlined"
            />
            <TextField
              className={classes.textField}
              style={{ flex: 0.25 }}
              id="template_status"
              select
              label="Estado"
              value={
                TemplateStatusMap.get(templateStatus || TEMPLATE_STATUS.DRAFT)
                  .name
              }
              onChange={templateStatusChange}
              helperText="Selecciona el estado de la plantilla"
              variant="outlined"
            >
              {TemplateStatus.map((option: { id: number; name: string }) => (
                <MenuItem key={option.id} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.textField}
              style={{ flex: 0.25 }}
              id="template_types"
              select
              label="Categoria"
              value={
                TemplateTypesMap.get(templateTypes || TEMPLATE_TYPES.OTHERS)
                  .name
              }
              onChange={templateTypesChange}
              helperText="Selecciona la categoria de la plantilla"
              variant="outlined"
            >
              {TemplateTypes.map((option: { id: number; name: string }) => (
                <MenuItem key={option.id} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className={classes.row}>
            <TextField
              className={classes.textField}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTemplatePrice(Number(e.target.value))
              }
              style={{ flex: 0.25 }}
              id="price"
              type="number"
              value={templatePrice}
              label="Precio"
              variant="outlined"
            />
            <TextField
              className={classes.textField}
              style={{ flex: 0.75 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTemplateDescription(e.target.value)
              }
              id="description"
              label="Descripción"
              multiline
              rows={2}
              value={templateDescription}
              variant="outlined"
            />
          </div>
        </Paper>

        <div style={{ marginBottom: '3vh' }}>
          <SunEditor
            lang="en"
            width="100%"
            height="450"
            defaultValue={HTMLCode}
            setDefaultStyle="font-family: OpenSans; font-size: 12px;"
            autoFocus={true}
            setOptions={{
              /* plugins: [
                align,
                fontColor,
                hiliteColor,
                list,
                table,
                formatBlock,
                textStyle,
                image,
                imageGallery,
              ], */
              font: ['OpenSans'],
              buttonList: [
                ['bold', 'underline', 'italic'],
                ['font', 'fontSize', 'formatBlock'],
                ['align', 'lineHeight', 'removeFormat'],
                ['textStyle'],
                ['fullScreen', 'codeView'],
                '/',
              ],
            }}
            onChange={handleChange}
          />
        </div>

        {/* <MenuBlocks blocks={blocks || []} setBlocks={setBlocks} />
        <ListBlocks
          blocks={blocks || []}
          setBlocks={setBlocks}
          inputs={inputs || []}
        /> */}

        <MenuInputs
          inputs={inputs}
          blocks={blocks || []}
          setBlocks={setBlocks}
          setInputs={setInputs}
        />
        <ListInputs inputs={inputs || []} setInputs={setInputs} />

        <Button
          type="submit"
          onClick={onSubmit}
          // disabled={}
          fullWidth
          variant="contained"
          className={classes.submit}
        >
          {template ? 'GUARDAR' : 'CREAR'}
        </Button>
        <Button
          onClick={() => history.push('/docengine')}
          // disabled={}
          fullWidth
          variant="contained"
          className={classes.submit}
        >
          VOLVER
        </Button>
        {template && (
          <Button
            onClick={onDelete}
            // disabled={}
            fullWidth
            variant="contained"
            className={classes.submit}
          >
            ELIMINAR
          </Button>
        )}
      </Container>
    </>
  )
}

export default TemplateEditor